import type { OwnerSettingKey, PlaceStatus, UnitLevel } from "./warehouse.api";

// The words of the WMS master data as the screens show them: keys only, resolved with t() at
// render. The values the API knows are in services/api/src/modules/warehouse/warehouseValues.ts.

export const PLACE_KINDS = [
  "receiving",
  "bulk",
  "floor",
  "pick",
  "pallet",
  "staging",
  "dock",
  "lost_found",
  "damaged",
  "inspection",
  "replenishment",
] as const;

/** The kinds that stand in a rack: what a series makes. */
export const RACK_KINDS = ["bulk", "pick", "pallet", "replenishment"] as const;

export const PLACE_KIND_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  PLACE_KINDS.map((kind) => [kind, `warehouse.kind.${kind}`]),
);

export const STORAGE_PROPERTIES = ["cooled", "frozen", "hazardous", "allergens", "hum", "non_hum", "pharma", "pharma_non_hum", "drum", "feed", "ground"] as const;
/** Everything but the temperature: shown after it. */
export const EXTRA_PROPERTIES = STORAGE_PROPERTIES.filter((property) => property !== "cooled" && property !== "frozen");

export const PROPERTY_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  STORAGE_PROPERTIES.map((property) => [property, `warehouse.property.${property}`]),
);

export const BLOCK_REASONS = ["damaged", "quality", "customer", "count_difference", "question", "other"] as const;

export const BLOCK_REASON_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  BLOCK_REASONS.map((reason) => [reason, `warehouse.blockReason.${reason}`]),
);

// The WMS words on the eight existing status colours (handoff §7). Reserved (athub) comes with stock.
export const PLACE_STATUS: Record<PlaceStatus, { className: string; labelKey: string }> = {
  free: { className: "wl-status--planned", labelKey: "warehouse.status.free" },
  occupied: { className: "wl-status--delivered", labelKey: "warehouse.status.occupied" },
  blocked: { className: "wl-status--onhold", labelKey: "warehouse.status.blocked" },
  out_of_use: { className: "wl-status--cancelled", labelKey: "warehouse.status.outOfUse" },
};

export const PLACE_STATUS_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  Object.entries(PLACE_STATUS).map(([status, look]) => [status, look.labelKey]),
);

// ─── Units ───────────────────────────────────────────────────────────────────

export const UNIT_LEVELS: readonly UnitLevel[] = ["cu", "tu", "du"];
export const UNITS_OF_MEASURE = ["PCE", "ST", "CT", "BX", "BG", "BD", "KT", "PF", "KGM"] as const;

/** "stuk", "doos", "pallet": the word for a unit of measure, with a count for its plural. */
export function uomKey(unitOfMeasure: string): string {
  return (UNITS_OF_MEASURE as readonly string[]).includes(unitOfMeasure) ? `warehouse.uom.${unitOfMeasure}` : "warehouse.uom.other";
}

// ─── Settings ────────────────────────────────────────────────────────────────

export type SettingKind = { type: "boolean" } | { type: "options"; options: readonly string[] };

export interface SettingDef {
  key: OwnerSettingKey;
  kind: SettingKind;
}

/** The settings of the matrix, in its order (handoff §4.3). */
export const OWNER_SETTINGS: readonly SettingDef[] = [
  { key: "pickMode", kind: { type: "options", options: ["reserve", "free"] } },
  { key: "batchPicking", kind: { type: "boolean" } },
  { key: "stackPicking", kind: { type: "boolean" } },
  { key: "mergePartials", kind: { type: "options", options: ["none", "same_batch", "mixed_batches"] } },
  { key: "oneBatchPerPallet", kind: { type: "boolean" } },
  { key: "pickByLocationScan", kind: { type: "boolean" } },
  { key: "palletTypeAtReceipt", kind: { type: "boolean" } },
  // Het PIC-bericht (docs/wms-fc-optimus.md §7.4 stap 15): batches fixed and reported for a COKZ inspection.
  { key: "pickReport", kind: { type: "boolean" } },
  // De THT-controle per afnemer (docs/wms-codebreuk.md V7): never a shorter best-before than the receiver had.
  { key: "deliveredCheck", kind: { type: "boolean" } },
];

export const REPLENISH_MODES = ["replace", "top_up"] as const;
/** Warehouse → Orders opens on a week of load days (afhalen) or of delivery days (leveren). */
export const OUTBOUND_DAYS = ["load", "delivery"] as const;

/** "Picken", "Batchpicken" … */
export function settingLabelKey(key: string): string {
  return `warehouse.setting.${key}`;
}

/** What it is, under its name. */
export function settingHintKey(key: string): string {
  return `warehouse.setting.${key}Hint`;
}

/** The label of one value of a setting: "Reserveren", "Alleen dezelfde batch", "Aan". */
export function settingValueKey(key: string, value: string | boolean): string {
  if (typeof value === "boolean") return value ? "warehouse.settingOn" : "warehouse.settingOff";
  return `warehouse.settingValue.${key}.${value}`;
}

/** What the floor notices of a value, in one sentence (handoff §4.3). */
export function settingEffectKey(key: string, value: string | boolean): string {
  return `warehouse.settingEffect.${key}.${String(value)}`;
}

// ─── Stock ───────────────────────────────────────────────────────────────────

// A pallet in Voorraad on the status colours (handoff §7, decision 5): free green, blocked mustard.
// Reserved (athub) and being picked (intransit) come with reserving and picking.
export const STOCK_STATUS: Record<string, { className: string; labelKey: string }> = {
  free: { className: "wl-status--delivered", labelKey: "warehouse.status.free" },
  blocked: { className: "wl-status--onhold", labelKey: "warehouse.status.blocked" },
  // Neither free nor picked from, and still the customer's until written off (§18).
  lost_found: { className: "wl-status--exception", labelKey: "warehouse.status.lostFound" },
};

export const STOCK_STATUS_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  Object.entries(STOCK_STATUS).map(([status, look]) => [status, look.labelKey]),
);

export const LABEL_SOURCE_LABEL_KEYS: Record<string, string> = {
  own: "warehouse.stock.labelOwn",
  customer: "warehouse.stock.labelCustomer",
};

/** "Overgenomen uit Scoretrace", "Geblokkeerd" …: what happened to a pallet. */
export function eventKindKey(kind: string): string {
  return `warehouse.event.${kind}`;
}

/** Where a block sits that stops a line. */
export const BLOCK_ON_LABEL_KEYS: Record<string, string> = {
  line: "warehouse.stock.blockOnLine",
  unit: "warehouse.stock.blockOnUnit",
  place: "warehouse.stock.blockOnPlace",
};
